// src/models/OfferModel.js

/**    
 * Standard structure for offer data throughout the application    
 * @typedef {Object} OfferModel    
 */    
export const createEmptyOffer = () => ({    
  // Basic information    
  name: '',    
  price: 0,    
  quantity: 1,    
  revenue: 0, // Calculated: price * quantity    
   
  // Cost categories    
  costs: {    
    directCosts: 0,    
    laborCosts: 0,    
    marketingCosts: 0,    
    toolsCosts: 0,    
    otherCosts: 0    
  },    
      
  // Team and time tracking    
  teamMembers: [], // [{name, hourlyRate, hours}] - hours per sale
  fixedMonthlyTime: [], // NEW: [{taskName, monthlyHours, hourlyRate}] - fixed monthly hours
  timeRequired: 0, // Total time for delivery    
      
  // Direct costs breakdown    
  directCostItems: [], // [{name, cost, notes}]    
      
  // Overhead allocation    
  overheadPercentage: 0,    
  allocationMethod: 'revenue', // 'revenue', 'time', or 'custom'    
  monthlyOverhead: {    
    software: 0,    
    workspace: 0,    
    adminStaff: 0,    
    marketing: 0,    
    other: 0    
  },    
      
  // Calculated fields (will be computed when needed)    
  totalCosts: 0,     
  profit: 0,    
  profitMargin: 0    
});  

/**  
 * Calculates overhead cost based on selected allocation method  
 * @param {Object} offer - The offer object  
 * @param {Object} businessData - Optional business data for revenue/time-based allocation  
 * @returns {number} - The calculated overhead cost  
 */
export const calculateOverheadCost = (offer, businessData = null) => {  
  if (!offer || !offer.monthlyOverhead) return 0;  
    
  // Calculate total monthly overhead  
  const totalMonthlyOverhead = Object.values(offer.monthlyOverhead)  
    .reduce((sum, cost) => sum + Number(cost || 0), 0);  
    
  // Get quantity  
  const quantity = Number(offer.quantity || 1);  
    
  // Different allocation methods  
  switch (offer.allocationMethod) {  
    case 'revenue':  
      // Revenue-based allocation  
      if (businessData && businessData.offers && businessData.offers.length > 0) {  
        // Calculate total revenue across all offers  
        const totalRevenue = businessData.offers.reduce((sum, o) =>   
          sum + (Number(o.price || 0) * Number(o.quantity || 1)), 0);  
          
        if (totalRevenue > 0) {  
          // Calculate this offer's revenue  
          const offerRevenue = Number(offer.price || 0) * quantity;  
          const revenueProportion = offerRevenue / totalRevenue;  
            
          return (totalMonthlyOverhead * revenueProportion) / quantity;  
        }  
      }  
        
      // Fallback to default percentage if no business data  
      return (totalMonthlyOverhead * 0.1) / quantity;  
        
    case 'time':  
      // Time-based allocation - UPDATED to include both variable and fixed time
      if (businessData && businessData.offers && businessData.offers.length > 0) {  
        // Calculate total time across all offers (variable + fixed)
        const totalTime = businessData.offers.reduce((sum, o) => {
          let offerTime = 0;
          
          // Variable time (hours per sale * quantity)
          if (Array.isArray(o.teamMembers)) {
            const variableHours = o.teamMembers.reduce((hrs, member) => 
              hrs + Number(member.hours || 0), 0);
            offerTime += variableHours * Number(o.quantity || 1);
          }
          
          // Fixed monthly time (not affected by quantity)
          if (Array.isArray(o.fixedMonthlyTime)) {
            const fixedHours = o.fixedMonthlyTime.reduce((hrs, task) => 
              hrs + Number(task.monthlyHours || 0), 0);
            offerTime += fixedHours;
          }
          
          return sum + offerTime;
        }, 0);
          
        if (totalTime > 0) {  
          // Calculate this offer's total time investment
          let offerTotalTime = 0;
          
          // Variable time for this offer
          if (Array.isArray(offer.teamMembers)) {
            const variableHours = offer.teamMembers.reduce((sum, member) => 
              sum + Number(member.hours || 0), 0);
            offerTotalTime += variableHours * quantity;
          }
          
          // Fixed monthly time for this offer
          if (Array.isArray(offer.fixedMonthlyTime)) {
            const fixedHours = offer.fixedMonthlyTime.reduce((sum, task) => 
              sum + Number(task.monthlyHours || 0), 0);
            offerTotalTime += fixedHours;
          }
          
          const timeProportion = offerTotalTime / totalTime;  
            
          return (totalMonthlyOverhead * timeProportion) / quantity;  
        }  
      }  
        
      // Fallback  
      return (totalMonthlyOverhead * 0.1) / quantity;  
        
    case 'custom':  
      // Custom percentage  
      const percentage = Number(offer.overheadPercentage || 0) / 100;  
      return (totalMonthlyOverhead * percentage) / quantity;  
        
    default:  
      return 0;  
  }  
};

/**  
 * Calculates total costs for an offer - SINGLE SOURCE OF TRUTH  
 * @param {Object} offer - The offer object  
 * @param {Object} businessData - Optional business data for overhead calculations  
 * @returns {number} The total costs  
 */  
export const calculateTotalCosts = (offer, businessData = null) => {  
  if (!offer) return 0;

  let teamCosts = 0;
  let fixedTimeCosts = 0; // NEW
  let directCosts = 0;
  let overheadCost = 0;
  let legacyCosts = 0;

  // Check if this offer has been through detailed Cost Discovery
  const hasDetailedCosts = (offer.has_detailed_costs === true) ||
    (Array.isArray(offer.teamMembers) && offer.teamMembers.length > 0) ||
    (Array.isArray(offer.directCostItems) && offer.directCostItems.length > 0) ||
    (Array.isArray(offer.fixedMonthlyTime) && offer.fixedMonthlyTime.length > 0); // NEW

  if (hasDetailedCosts) {
    // This offer has detailed costs from Cost Discovery
    
    // Calculate variable team costs (per sale)
    if (Array.isArray(offer.teamMembers) && offer.teamMembers.length > 0) {
      teamCosts = offer.teamMembers.reduce((sum, member) => {
        const hourlyRate = Number(member.hourlyRate || 0);
        const hours = Number(member.hours || 0);
        return sum + (hourlyRate * hours);
      }, 0);
    }
    
    // NEW: Calculate fixed monthly time costs (divided by quantity for per-unit cost)
    if (Array.isArray(offer.fixedMonthlyTime) && offer.fixedMonthlyTime.length > 0) {
      const quantity = Number(offer.quantity || 1);
      const totalFixedCosts = offer.fixedMonthlyTime.reduce((sum, task) => {
        const monthlyHours = Number(task.monthlyHours || 0);
        const hourlyRate = Number(task.hourlyRate || 0);
        return sum + (monthlyHours * hourlyRate);
      }, 0);
      fixedTimeCosts = quantity > 0 ? totalFixedCosts / quantity : 0;
    }

    // Calculate direct costs from items
    if (Array.isArray(offer.directCostItems) && offer.directCostItems.length > 0) {
      directCosts = offer.directCostItems.reduce((sum, item) => {
        return sum + Number(item.cost || 0);
      }, 0);
    }

    // Calculate overhead cost
    overheadCost = calculateOverheadCost(offer, businessData);

    // For detailed costs, include all components
    return teamCosts + fixedTimeCosts + directCosts + overheadCost;
  } else {
    // This offer only has basic/legacy costs
    
    // First priority: use total_costs if available
    if (offer.total_costs !== undefined && offer.total_costs !== null && !isNaN(Number(offer.total_costs))) {
      legacyCosts = Number(offer.total_costs);
    }
    // Second priority: sum up legacy costs object
    else if (offer.costs) {
      Object.values(offer.costs || {}).forEach(cost => {
        legacyCosts += Number(cost || 0);
      });
    }

    // For basic offers, check if overhead should be added from shared data
    if (businessData && businessData.offers) {
      // Find if ANY offer has overhead data
      const offerWithOverhead = businessData.offers.find(o =>
        o.monthlyOverhead &&
        Object.values(o.monthlyOverhead).some(v => Number(v) > 0)
      );

      if (offerWithOverhead) {
        // Apply shared overhead to this basic offer
        const tempOffer = {
          ...offer,
          monthlyOverhead: offerWithOverhead.monthlyOverhead,
          allocationMethod: offerWithOverhead.allocationMethod || 'revenue',
          overheadPercentage: offerWithOverhead.overheadPercentage || 0
        };
        overheadCost = calculateOverheadCost(tempOffer, businessData);
      }
    }

    return legacyCosts + overheadCost;
  }
};

/**
 * Calculates profit margin consistently across the application
 * @param {Object} offer - The offer object with price and cost data
 * @param {Object} businessData - Optional business data for overhead calculations
 * @returns {number} The calculated profit margin percentage
 */
export const calculateProfitMargin = (offer, businessData = null) => {
  if (!offer) return 0;
  
  const price = Number(offer.price) || 0;
  if (price <= 0) return 0;
  
  // Use the new single source of truth
  const totalCosts = calculateTotalCosts(offer, businessData);
  const profit = price - totalCosts;
  const profitMargin = (profit / price) * 100;
  
  return profitMargin;
};

/**
 * Calculate derived values for an offer
 * @param {OfferModel} offer - The offer to calculate values for
 * @param {Object} businessData - Optional business data for calculations
 * @returns {OfferModel} The updated offer with calculated fields
 */
export const calculateOfferMetrics = (offer, businessData = null) => {
  // Copy the offer to avoid mutating the original
  const updatedOffer = { ...offer };
  
  // Calculate revenue
  updatedOffer.revenue = Number(offer.price || 0) * Number(offer.quantity || 1);
  
  // Determine if this offer has been through detailed Cost Discovery
  const hasDetailedCosts = offer.has_detailed_costs === true;
  
  // Find shared overhead from any offer that has it
  let sharedOverheadData = null;
  if (businessData && businessData.offers) {
    const offerWithOverhead = businessData.offers.find(o => 
      o.monthlyOverhead && 
      Object.keys(o.monthlyOverhead).length > 0 &&
      Object.values(o.monthlyOverhead).some(v => Number(v) > 0) &&
      o.allocationMethod
    );
    
    if (offerWithOverhead) {
      sharedOverheadData = {
        monthlyOverhead: offerWithOverhead.monthlyOverhead,
        allocationMethod: offerWithOverhead.allocationMethod,
        overheadPercentage: offerWithOverhead.overheadPercentage
      };
    }
  }
  
  if (!hasDetailedCosts) {
    // This offer only has initial estimates or legacy costs
    let baseCosts = 0;
    
    // Use total_costs if available
    if (offer.total_costs > 0) {
      baseCosts = Number(offer.total_costs || 0);
    } 
    // Otherwise sum up legacy costs
    else if (offer.costs) {
      baseCosts = Object.values(offer.costs || {}).reduce(
        (sum, cost) => sum + Number(cost || 0), 
        0
      );
    }
    
    // Calculate allocated overhead
    let allocatedOverhead = 0;
    if (sharedOverheadData) {
      // Create a temporary offer with shared overhead data
      const tempOffer = {
        ...offer,
        monthlyOverhead: sharedOverheadData.monthlyOverhead,
        allocationMethod: sharedOverheadData.allocationMethod,
        overheadPercentage: sharedOverheadData.overheadPercentage,
        price: offer.price || 0,
        quantity: offer.quantity || 1
      };
      
      // If using time-based allocation but this offer has no team members defined,
      // we need to check if it has legacy time data
      if (sharedOverheadData.allocationMethod === 'time' && 
          (!Array.isArray(offer.teamMembers) || offer.teamMembers.length === 0) &&
          (!Array.isArray(offer.fixedMonthlyTime) || offer.fixedMonthlyTime.length === 0)) { // UPDATED
        // Check for legacy labor costs that might indicate time investment
        if (offer.costs && offer.costs.laborCosts > 0) {
          // Estimate hours based on a reasonable hourly rate
          const estimatedHourlyRate = 75; // Default assumption
          const estimatedHours = offer.costs.laborCosts / estimatedHourlyRate;
          tempOffer.teamMembers = [{
            name: 'Team',
            hourlyRate: estimatedHourlyRate,
            hours: estimatedHours
          }];
        }
      }
      
      allocatedOverhead = calculateOverheadCost(tempOffer, businessData);
    }
    
    // Total = base costs + allocated overhead
    updatedOffer.totalCosts = baseCosts + allocatedOverhead;
    
  } else {
    // This offer has detailed costs
    let teamCosts = 0;
    let fixedTimeCosts = 0; // NEW
    let directCosts = 0;
    let overheadCost = 0;
    
    // Calculate variable team costs
    if (Array.isArray(offer.teamMembers) && offer.teamMembers.length > 0) {
      teamCosts = offer.teamMembers.reduce((sum, member) => {
        const hourlyRate = Number(member.hourlyRate || 0);
        const hours = Number(member.hours || 0);
        return sum + (hourlyRate * hours);
      }, 0);
    }
    
    // NEW: Calculate fixed monthly time costs
    if (Array.isArray(offer.fixedMonthlyTime) && offer.fixedMonthlyTime.length > 0) {
      const quantity = Number(offer.quantity || 1);
      const totalFixedCosts = offer.fixedMonthlyTime.reduce((sum, task) => {
        const monthlyHours = Number(task.monthlyHours || 0);
        const hourlyRate = Number(task.hourlyRate || 0);
        return sum + (monthlyHours * hourlyRate);
      }, 0);
      fixedTimeCosts = quantity > 0 ? totalFixedCosts / quantity : 0;
    }
    
    // Calculate direct costs
    if (Array.isArray(offer.directCostItems) && offer.directCostItems.length > 0) {
      directCosts = offer.directCostItems.reduce((sum, item) => {
        return sum + Number(item.cost || 0);
      }, 0);
    }
    
    // Calculate overhead
    if (offer.monthlyOverhead && Object.values(offer.monthlyOverhead).some(v => Number(v) > 0)) {
      // This offer has its own overhead data
      overheadCost = calculateOverheadCost(offer, businessData);
    } else if (sharedOverheadData) {
      // Use shared overhead from another offer
      const tempOffer = {
        ...offer,
        monthlyOverhead: sharedOverheadData.monthlyOverhead,
        allocationMethod: sharedOverheadData.allocationMethod,
        overheadPercentage: sharedOverheadData.overheadPercentage
      };
      overheadCost = calculateOverheadCost(tempOffer, businessData);
    }
    
    // If we still have no costs but have legacy costs, use those
    if ((teamCosts + fixedTimeCosts + directCosts + overheadCost) === 0 && offer.costs) {
      const legacyCosts = Object.values(offer.costs || {}).reduce(
        (sum, cost) => sum + Number(cost || 0), 
        0
      );
      updatedOffer.totalCosts = legacyCosts;
    } else {
      // Total = variable team + fixed team + direct + overhead
      updatedOffer.totalCosts = teamCosts + fixedTimeCosts + directCosts + overheadCost;
    }
  }
  
  // Calculate profit and margin
  updatedOffer.profit = updatedOffer.revenue - updatedOffer.totalCosts;
  updatedOffer.profitMargin = updatedOffer.revenue > 0 ? 
    (updatedOffer.profit / updatedOffer.revenue) * 100 : 0;
  
  return updatedOffer;
};   
    
/**    
 * Convert a basic offer to the full model    
 * @param {Object} basicOffer - A partial offer with minimal fields    
 * @returns {OfferModel} The complete offer model    
 */    
export const convertToFullOfferModel = (basicOffer) => {      
  const emptyOffer = createEmptyOffer();  
    
  // Log and handle invalid input  
  if (!basicOffer) {  
    console.warn("convertToFullOfferModel received invalid input:", basicOffer);  
    return emptyOffer;  
  }  
        
  // Deep clone to avoid reference issues  
  const safeBasicOffer = JSON.parse(JSON.stringify(basicOffer));  
        
  // Merge the basic offer with the empty template      
  const mergedOffer = {      
    ...emptyOffer,      
    ...safeBasicOffer,      
    // Make sure nested objects are properly merged    
    costs: {      
      ...emptyOffer.costs,      
      ...(safeBasicOffer.costs || {})      
    },      
    monthlyOverhead: {      
      ...emptyOffer.monthlyOverhead,      
      ...(safeBasicOffer.monthlyOverhead || {})      
    },    
    // Make sure arrays are properly handled    
    teamMembers: safeBasicOffer.teamMembers || emptyOffer.teamMembers,    
    directCostItems: safeBasicOffer.directCostItems || emptyOffer.directCostItems,
    fixedMonthlyTime: safeBasicOffer.fixedMonthlyTime || emptyOffer.fixedMonthlyTime // NEW
  };      
        
  // Calculate all the derived metrics      
  return calculateOfferMetrics(mergedOffer);      
};

/**
 * Gets a breakdown of costs by category
 * @param {Object} offer - The offer object
 * @param {Object} businessData - Optional business data
 * @returns {Object} Cost breakdown by category
 */
export const getCostBreakdown = (offer, businessData = null) => {
  if (!offer) return {
    teamCosts: 0,
    fixedTimeCosts: 0, // NEW
    directCosts: 0,
    overheadCosts: 0,
    totalCosts: 0
  };

  let teamCosts = 0;
  let fixedTimeCosts = 0; // NEW
  let directCosts = 0;
  let overheadCosts = 0;

  const hasDetailedCosts = (offer.has_detailed_costs === true) ||
    (Array.isArray(offer.teamMembers) && offer.teamMembers.length > 0) ||
    (Array.isArray(offer.directCostItems) && offer.directCostItems.length > 0) ||
    (Array.isArray(offer.fixedMonthlyTime) && offer.fixedMonthlyTime.length > 0); // NEW

  if (hasDetailedCosts) {
    // Calculate variable team costs
    if (Array.isArray(offer.teamMembers) && offer.teamMembers.length > 0) {
      teamCosts = offer.teamMembers.reduce((sum, member) => {
        const hourlyRate = Number(member.hourlyRate || 0);
        const hours = Number(member.hours || 0);
        return sum + (hourlyRate * hours);
      }, 0);
    }
    
    // NEW: Calculate fixed monthly time costs
    if (Array.isArray(offer.fixedMonthlyTime) && offer.fixedMonthlyTime.length > 0) {
      const quantity = Number(offer.quantity || 1);
      const totalFixedCosts = offer.fixedMonthlyTime.reduce((sum, task) => {
        const monthlyHours = Number(task.monthlyHours || 0);
        const hourlyRate = Number(task.hourlyRate || 0);
        return sum + (monthlyHours * hourlyRate);
      }, 0);
      fixedTimeCosts = quantity > 0 ? totalFixedCosts / quantity : 0;
    }

    // Calculate direct costs
    if (Array.isArray(offer.directCostItems) && offer.directCostItems.length > 0) {
      directCosts = offer.directCostItems.reduce((sum, item) => {
        return sum + Number(item.cost || 0);
      }, 0);
    }

    // Calculate overhead
    overheadCosts = calculateOverheadCost(offer, businessData);
  } else {
    // Legacy costs - we can't break them down, so put everything in directCosts
    const totalCosts = calculateTotalCosts(offer, businessData);
    directCosts = totalCosts; // Put all legacy costs in direct costs
  }

  return {
    teamCosts,
    fixedTimeCosts, // NEW
    directCosts,
    overheadCosts,
    totalCosts: teamCosts + fixedTimeCosts + directCosts + overheadCosts
  };
};

/**
 * Validates an offer has all required fields
 * @param {Object} offer - The offer to validate
 * @returns {Object} Validation result with isValid and errors array
 */
export const validateOffer = (offer) => {
  const errors = [];
  
  if (!offer.name || offer.name.trim() === '') {
    errors.push('Offer name is required');
  }
  
  if (!offer.price || Number(offer.price) <= 0) {
    errors.push('Price must be greater than 0');
  }
  
  if (!offer.quantity || Number(offer.quantity) <= 0) {
    errors.push('Quantity must be greater than 0');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Calculates the profit for an offer
 * @param {Object} offer - The offer object
 * @param {Object} businessData - Optional business data
 * @returns {number} The profit amount
 */
export const calculateProfit = (offer, businessData = null) => {
  if (!offer) return 0;
  
  const price = Number(offer.price) || 0;
  const totalCosts = calculateTotalCosts(offer, businessData);
  
  return price - totalCosts;
};

/**
 * Calculates the revenue for an offer
 * @param {Object} offer - The offer object
 * @returns {number} The revenue amount
 */
export const calculateRevenue = (offer) => {
  if (!offer) return 0;
  
  const price = Number(offer.price) || 0;
  const quantity = Number(offer.quantity) || 1;
  
  return price * quantity;
};